import React, { useState } from 'react';
import { Box, Button, Typography, Alert } from '@mui/material';
import { useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import type { Project } from '../types';
import { createDefaultProject, validateProject } from '../types';
import { Loading } from '../components/Loading';
import { ProjectEdit } from '../components/ProjectEdit';
import { Layout } from '../components/Layout';

export const NewProject: React.FC = () => {
  const [project, setProject] = useState<Project>(createDefaultProject());
  const [controlNodes, setControlNodes] = useState<Record<string, number>>({});
  const [workerNodes, setWorkerNodes] = useState<Record<string, number>>({});
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Load node types and applications
  const { data: nodeTypes, isLoading: nodeTypesLoading } = useQuery({
    queryKey: ['nodeTypes'],
    queryFn: () => api.getNodeTypes(),
  });

  const { data: applications, isLoading: applicationsLoading } = useQuery({
    queryKey: ['applications'],
    queryFn: () => api.getApplications(),
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: { project: Project; controlNodes: Record<string, number>; workerNodes: Record<string, number> }) =>
      api.createProject(data.project, data.controlNodes, data.workerNodes),
    onSuccess: (response) => {
      if (response) {
        queryClient.invalidateQueries({ queryKey: ['projects'] });
        navigate(`/projects/${response.projectId}`);
      } else {
        setError('Failed to create project. Please try again.');
      }
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to create project. Please try again.');
    },
  });

  const validation = validateProject(project, controlNodes, workerNodes);
  const canSave = !validation.some((v) => v.preventContinue);

  const handleSave = () => {
    if (!canSave) return;
    setError(null);
    createMutation.mutate({ project, controlNodes, workerNodes });
  };

  const handleCancel = () => {
    navigate('/');
  };

  if (nodeTypesLoading || applicationsLoading || !nodeTypes || !applications) {
    return (
      <Layout>
        <Loading />
      </Layout>
    );
  }

  return (
    <Layout>
      <Typography variant="h4" component="h1" gutterBottom>
        Create a New Project
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <ProjectEdit
        project={project}
        controlNodes={controlNodes}
        workerNodes={workerNodes}
        nodes={[]}
        nodesToDelete={[]}
        onChange={setProject}
        onControlNodesChange={setControlNodes}
        onWorkerNodesChange={setWorkerNodes}
        onNodesToDeleteChange={() => {}}
        nodeTypes={nodeTypes}
        applications={applications}
        validation={validation}
      />

      <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSave}
          disabled={!canSave || createMutation.isPending}
        >
          {createMutation.isPending ? 'Creating...' : 'Create'}
        </Button>
        <Button variant="outlined" size="large" onClick={handleCancel}>
          Cancel
        </Button>
      </Box>
    </Layout>
  );
};
